import { Duration, Instant, LocalDateTime, LocalTime } from "@js-joda/core";
import { isDefined } from "./components/Generic/IsDefined";
import {
  PlanOfCareItemId,
  RequiredDutySheetTask,
  VisitInstanceDetails,
  VisitInstanceId,
} from "./messages";
import { STRINGS } from "./strings";
import { translate } from "./translate";
import { List } from "./utils/List";

export function isInClockInTimeRange(now: Instant, startTime: Instant, endTime: Instant): boolean {
  const startOfRange = startTime.minus(Duration.ofMinutes(480));
  const endOfRange = endTime.plus(Duration.ofMinutes(480));

  return now.isAfter(startOfRange) && now.isBefore(endOfRange);
}

export function isInClockOutTimeRange(now: Instant, startTime: Instant, endTime: Instant): boolean {
  const startOfRange = startTime.minus(Duration.ofMinutes(480));
  const endOfRange = endTime.plus(Duration.ofMinutes(480));

  return now.isAfter(startOfRange) && now.isBefore(endOfRange);
}

export const canClockFor = (scheduleTime: Instant) => {
  const now = Instant.now();
  const timeRange = scheduleTime.minus(Duration.ofHours(8));
  return now.isAfter(timeRange);
};

export interface DutiesPartition {
  required: List<RequiredDutySheetTask>;
  optional: List<RequiredDutySheetTask>;
}

export type ClockStrategy =
  | {
      valid: true;
      visitType: "Present" | "Future" | "Past";
      strategy: "GeoLocation" | "Timesheet" | "ClockedNotApproved";
    }
  | {
      valid: false;
      visitType: "Present" | "Future" | "Past";
      title: string;
      message: string;
    };

export const getClockInStrategy = (visit: VisitInstanceDetails): ClockStrategy => {
  const isFutureVisit = !canClockFor(visit.startTimeUTC);

  if (isFutureVisit) {
    return {
      valid: false,
      visitType: "Future",
      title: translate(STRINGS.SOMETHING_WENT_WRONG),
      message: translate(STRINGS.CANT_CLOCK_IN),
    };
  }

  const hasAlreadyClockedIn = visit.clockInTime !== null;
  const isPresentVisit = isInClockInTimeRange(Instant.now(), visit.startTimeUTC, visit.endTimeUTC);

  if (!hasAlreadyClockedIn) {
    return {
      valid: true,
      visitType: isPresentVisit ? "Present" : "Past",
      strategy: isPresentVisit ? "GeoLocation" : "Timesheet",
    };
  }

  if (!isPresentVisit && !visit.manualClockTimeEditApproved) {
    return {
      valid: true,
      visitType: "Past",
      strategy: "ClockedNotApproved",
    };
  }

  return {
    valid: false,
    visitType: isPresentVisit ? "Present" : "Past",
    title: translate(STRINGS.ALREADY_CLOCKED_IN),
    message: translate(STRINGS.CANT_CLOCK_IN),
  };
};

export const getClockOutStrategy = (visit: VisitInstanceDetails): ClockStrategy => {
  const isFutureVisit = !canClockFor(visit.endTimeUTC);

  if (isFutureVisit) {
    return {
      valid: false,
      visitType: "Future",
      title: translate(STRINGS.SOMETHING_WENT_WRONG),
      message: translate(STRINGS.CANT_CLOCK_OUT),
    };
  }

  const hasAlreadyClockedOut = isDefined(visit.clockOutTime) || visit.checkOutTime !== null;
  const isPresentVisit = isInClockOutTimeRange(Instant.now(), visit.startTimeUTC, visit.endTimeUTC);

  if (!hasAlreadyClockedOut) {
    return {
      valid: true,
      visitType: isPresentVisit ? "Present" : "Past",
      strategy: isPresentVisit ? "GeoLocation" : "Timesheet",
    };
  }

  if (!isPresentVisit && !visit.manualClockTimeEditApproved) {
    return {
      valid: true,
      visitType: "Past",
      strategy: "ClockedNotApproved",
    };
  }

  return {
    valid: false,
    visitType: isPresentVisit ? "Present" : "Past",
    message: translate(STRINGS.ALREADY_CLOCKED_OUT),
    title: translate(STRINGS.CANT_CLOCK_OUT),
  };
};

export const checkIfTimeSheetStrategy = (
  clockInStrategy: ClockStrategy,
  clockOutStrategy: ClockStrategy
) => {
  return (
    (clockInStrategy.valid && clockInStrategy.strategy === "Timesheet") ||
    (clockOutStrategy.valid && clockOutStrategy.strategy === "Timesheet")
  );
};

export function isReminderRelevantForVisit(visit: VisitInstanceDetails, now: LocalDateTime) {
  if (
    (visit.clockInTime !== null && visit.clockOutTime !== null) ||
    visit.isDutySheetCompleted ||
    (!visit.publicVisitDetails.isBillingAgency && !visit.publicVisitDetails.isMedflytAgency)
  ) {
    return false;
  }

  const startTime = visit.startTimeLocal.minus(Duration.ofMinutes(30));
  const endTime = visit.endTimeLocal.plus(Duration.ofHours(2));
  return startTime.isBefore(now) && endTime.isAfter(now);
}

export function sortClockedVisitsByTime(visits: VisitInstanceDetails[]) {
  return visits.sort((a, b) => {
    if (a.clockInTime === null && b.clockInTime === null) {
      return 0;
    }

    if (a.clockInTime === null) {
      return 1;
    }

    if (b.clockInTime === null) {
      return -1;
    }

    return b.clockInTime.compareTo(a.clockInTime);
  });
}

export function trimPatientInitials(patientInitials: string) {
  return patientInitials.replace(/[^A-Z]/g, "");
}

export function getClockOutRangeStartTime(
  visitStartTime: LocalTime,
  visitEndTime: LocalTime
): LocalTime {
  return visitStartTime.isBefore(visitEndTime.minus(Duration.ofHours(8)))
    ? visitEndTime.minus(Duration.ofHours(8))
    : visitStartTime;
}

export function isClockOutStartTimeSameAsClockIn(
  visitStartTime: LocalTime,
  visitEndTime: LocalTime
): boolean {
  return getClockOutRangeStartTime(visitStartTime, visitEndTime).equals(visitStartTime);
}

export function getDutySheetVisitTaskKey(id: PlanOfCareItemId, visitInstanceId: VisitInstanceId) {
  return `duty-${id}-visit-${visitInstanceId}`;
}

export function partitionDuties(duties: List<RequiredDutySheetTask>): DutiesPartition {
  let required = List.empty<RequiredDutySheetTask>();
  let optional = List.empty<RequiredDutySheetTask>();
  for (const duty of duties) {
    if (duty.isRequired) {
      required = required.append(duty);
    } else {
      optional = optional.append(duty);
    }
  }
  return {
    required: required,
    optional: optional,
  };
}
